/** Local diagnostics only. No error messages, request bodies, query strings, filenames or DICOM metadata. */
import { RELEASE } from './release'

type Operation = 'image.load' | 'image.frame' | 'image.thumbnail' | 'image.edges' | 'http' | 'network' | 'react' | 'unhandled' | 'action'
interface Context {
  operation: Operation
  path?: string
  imageId?: string
  status?: number
  requestId?: string
}
interface Frame { file: string; line: number; column: number }
export interface ErrorReport {
  id: string
  operation: Operation
  error_type: string
  code: string
  route: string
  request_id?: string
  image_id?: string
  status?: number
  frames: Frame[]
  release: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// The words of the app's own routes and API paths (a test keeps it complete); IDs, codes and
// anything a user typed into a path are not among them, so they are replaced.
const SEGMENTS = new Set(
  `
  accept access accounts activate adjudicate adjudication adjudications admin admins affiliation agreement annotation
  annotation-jobs annotations answer answered api approvals archive assignments associations attributes audit auth
  authorize bench benchmark-approvals benchmark-participants benchmarks boxes browser-check bucket by-pixels callback
  cancel case-sets cases catalog categories changes clinical-reports close closed clouds codes collections comments
  comparison complete connectors contributions contributors courses credentials dashboard data datasets decision
  default delete departure describe device directory document editing editors education educators enrollments errors
  evaluations export exports external file files finish flags follow forum free-text freeze frozen frozen-collections
  goal grants guidelines heatmap help history images impact imports imprint inference inference-terms invitations jobs
  keys known labeling-profile labeling-profiles labels leaderboard learners learning-terms legal locations login logins
  logout lost majority me members metadata mine model-runs models moderation moderators moves new next nextcloud note
  notifications outside-access participants patients pedfx people personal photo poll polls posts predictions preview
  privacy processing profile progress proposals public-profile pyodide read reader-boxes readers recordings release
  remove rendered reopen report requests research-consent resolutions results retire return reviews roles runs schedule
  scheduling search second-factor segment session shared sharing sharing-policy shortcuts similar sites specialties
  speech speech-bench sso state status storage storage-locations storage-moves stream studies submissions summary support t
  tags tasks teaching terms thumbnail time-zone token-check tokens topics upload-location uploaded uploads users verify versions
  view warnings window work workspace
  `.trim().split(/\s+/),
)
export function diagnosticRoute(path: string): string {
  const clean = path.split(/[?#]/, 1)[0] ?? '/'
  if (!clean.startsWith('/') || clean.startsWith('//')) return '/external'
  return '/' + clean.split('/').filter(Boolean).slice(0, 12).map((part) => SEGMENTS.has(part) ? part : ':id').join('/')
}

function frames(error: unknown): Frame[] {
  const stack = error instanceof Error ? (error.stack ?? '').split('\n').slice(1).join('\n') : ''
  return [...stack.matchAll(/(?:\/|\\)([A-Za-z0-9_.-]{1,120}\.(?:js|mjs|ts|tsx))(?:\?[^\s():]*)?:(\d+):(\d+)/g)]
    .slice(0, 10).map((match) => ({ file: match[1]!, line: Number(match[2]), column: Number(match[3]) }))
}

/** Stable categories give useful diagnostics without sending possibly sensitive exception text. */
function code(error: unknown): string {
  const message = error instanceof Error ? error.message : ''
  if (message === 'Thumbnail could not be loaded') return 'thumbnail_load_failed'
  if (/VOI LUT/i.test(message)) return /Invalid|Incomplete|Malformed|Missing/.test(message) ? 'invalid_voi_lut' : 'unsupported_voi_lut'
  if (/Modality LUT/.test(message)) return 'unsupported_modality_lut'
  if (/transfer syntax/.test(message)) return 'unsupported_transfer_syntax'
  if (/Truncated|Malformed|Not a DICOM|pixel data|Image size mismatch/.test(message)) return 'invalid_dicom'
  if (/worker/i.test(message)) return 'image_worker_failed'
  if (/HTTP \d+/.test(message)) return 'image_download_failed'
  return 'unexpected_error'
}

const references = new WeakMap<object, string>()
export function errorReference(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const requestId = (error as { requestId?: string }).requestId
  return requestId && UUID.test(requestId) ? requestId : references.get(error)
}

function isExpectedError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const status = (error as Error & { status?: number }).status
  return error.name === 'AbortError' || (status !== undefined && status >= 400 && status < 500)
}

/** Injectable transport keeps reporting independent of API hooks and prevents reporting recursion. */
export function createErrorReporter(send: (events: ErrorReport[]) => Promise<boolean>, now = Date.now) {
  const queue: ErrorReport[] = []
  const seen = new Map<string, { at: number; id: string }>()
  let timer: ReturnType<typeof setTimeout> | undefined
  let sending = false
  let failures = 0
  let budgetStart = now()
  let count = 0
  const schedule = (delay = 1000) => { timer ??= setTimeout(() => { timer = undefined; void flush() }, delay) }
  async function flush() {
    if (timer) { clearTimeout(timer); timer = undefined }
    if (sending || !queue.length) return
    sending = true
    const batch = queue.splice(0, 10)
    let ok = false
    try { ok = await send(batch) } catch { /* never report reporting failures */ }
    sending = false
    if (ok) failures = 0
    else if (++failures < 3) queue.unshift(...batch)
    if (queue.length) schedule(ok ? 1000 : 30_000)
  }
  return {
    report(error: unknown, context: Context): string | undefined {
      if (isExpectedError(error)) return undefined
      const existing = errorReference(error)
      if (error && typeof error === 'object' && references.has(error)) return existing
      const at = now()
      for (const [key, value] of seen) if (at - value.at >= 60_000) seen.delete(key)
      if (at - budgetStart >= 60_000) { budgetStart = at; count = 0 }
      const route = diagnosticRoute(context.path ?? globalThis.location?.pathname ?? '/')
      const safeFrames = frames(error)
      const errorType = error instanceof Error && ['Error', 'TypeError', 'RangeError', 'SyntaxError', 'ReferenceError', 'ApiError', 'TimeoutError'].includes(error.name) ? error.name : 'Error'
      const errorCode = code(error)
      const signature = JSON.stringify([context.operation, route, context.imageId, context.status, errorType, errorCode, safeFrames])
      const duplicate = seen.get(signature)
      if (!duplicate && (count >= 20 || queue.length >= 40)) return undefined
      const id = duplicate?.id ?? existing ?? crypto.randomUUID()
      if (error && typeof error === 'object') references.set(error, id)
      if (duplicate) return id
      count++
      seen.set(signature, { at, id })
      queue.push({
        id, operation: context.operation, error_type: errorType, code: errorCode, route,
        request_id: context.requestId && UUID.test(context.requestId) ? context.requestId : undefined,
        image_id: context.imageId && UUID.test(context.imageId) ? context.imageId : undefined,
        status: context.status, frames: safeFrames,
        release: RELEASE,
      })
      schedule()
      return id
    },
    flush,
  }
}

export const errorReporter = createErrorReporter(async (events) => {
  const response = await fetch('/api/errors', {
    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }), signal: AbortSignal.timeout(5000), keepalive: true,
  })
  // Validation/auth refusals cannot be fixed by resending the same batch.
  return response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)
})

export function installErrorReporter(): () => void {
  const failure = (event: ErrorEvent) => errorReporter.report(event.error ?? new Error('Script error'), { operation: 'unhandled' })
  const rejection = (event: PromiseRejectionEvent) => errorReporter.report(event.reason, { operation: 'unhandled' })
  const leaving = () => { void errorReporter.flush() }
  window.addEventListener('error', failure)
  window.addEventListener('unhandledrejection', rejection)
  window.addEventListener('pagehide', leaving)
  return () => {
    window.removeEventListener('error', failure)
    window.removeEventListener('unhandledrejection', rejection)
    window.removeEventListener('pagehide', leaving)
  }
}
